'use client'

import type { ChainType } from '@polaris/core/models'
import { useAtom, useAtomValue } from 'jotai'
import { ChevronRight } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import { type Ref, useEffect, useMemo, useState } from 'react'
import { useMeasure } from 'react-use'
import { ButtonDiv } from '@/src/components/ui/button'
import { Dialog, DialogContent } from '@/src/components/ui/dialog'
import {
  closeOnboardingDialog,
  onboardingDialogAtom,
  openOnboardingDialog,
} from '@/src/stores/onboarding'
import { selectWalletsDialogChainTypeAtom } from '@/src/stores/select-wallets'
import { cn } from '@/src/utils/styles'
import { useConnectWalletsDialogContent } from '../connect-wallet/connect-wallets-dialog'
import { useLoginDialogContent } from './login-dialog'

// Height configuration constants
const HEIGHT_CONFIG = {
  // Footer heights
  LOGIN_FOOTER: 88,
} as const

interface OnboardingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  chainType?: ChainType
  isVercelPreview?: boolean
}

function OnboardingDialog({
  open,
  onOpenChange,
  chainType,
  isVercelPreview,
}: OnboardingDialogProps) {
  const [dialogState, setDialogState] = useAtom(onboardingDialogAtom)
  const mode = dialogState.mode
  const [enableAnimation, setEnableAnimation] = useState(false)

  const loginResult = useLoginDialogContent({
    isVercelPreview,
    mainCtaCopy: (
      <span>
        Welcome, <span className='italic'>Pioneer</span>
      </span>
    ),
  })

  const walletsResult = useConnectWalletsDialogContent({
    open,
    onOpenChange,
    chainType,
  })

  // Start animation after 1s delay when dialog opens
  // This prevents the contents from animating as the dialog is opened and renders
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setEnableAnimation(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
    setEnableAnimation(false)
  }, [open])

  // Wrap onOpenChange to reset mode when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen)
  }

  const loginFooter = (
    <button
      onClick={() => setDialogState(openOnboardingDialog('wallets'))}
      className='group relative h-28 overflow-hidden rounded-b-[32px] w-full'
      data-testid='start-without-account-btn'
    >
      <Image
        src='https://s3.polaris.app/artworks/girl-backpack-camping.png'
        alt='Start trading'
        fill
        className='object-cover absolute object-[25%_75%]'
        unoptimized
      />
      <div className='absolute inset-0 bg-black/40 transition-opacity duration-300 group-hover:opacity-20 dark:group-hover:opacity-0' />
      <div className='absolute inset-x-0 bottom-3 px-8 pb-4'>
        <div className='flex items-center justify-between'>
          <span className='font-semibold text-gray-50 dark:text-primary'>
            Start without an account
          </span>
          <div className='flex items-center gap-2 group-hover:gap-4 h-8 transition-all duration-100'>
            {walletsResult?.initialWalletOptions
              .slice(0, 3)
              .map(
                (wallet) =>
                  wallet.logoUrl && (
                    <Image
                      key={wallet.id}
                      src={wallet.logoUrl}
                      alt={wallet.displayName}
                      width={24}
                      height={24}
                      className='rounded-md'
                      unoptimized
                    />
                  ),
              )}
            <ChevronRight className='w-6 h-6 text-gray-50 dark:text-tertiary' />
          </div>
        </div>
      </div>
    </button>
  )

  const walletsFooter = (
    <motion.button
      type='button'
      onClick={() => setDialogState(openOnboardingDialog('login'))}
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      exit={{ y: 100 }}
      transition={{
        type: 'tween',
        ease: [0.34, 0.94, 0.1, 1],
        duration: 0.5,
        stiffness: 300,
        damping: 30,
      }}
      className='absolute h-24 left-0 right-0 bottom-0 z-20 overflow-hidden rounded-[32px] group'
    >
      <Image
        src='https://s3.polaris.app/artworks/girl-backpack-field-house.png'
        alt='Already have an account'
        fill
        className='object-cover absolute object-[25%_65%] transition-all duration-300 dark:group-hover:opacity-70'
        unoptimized
      />
      <div className='absolute inset-x-0 bottom-3 px-8 pb-4'>
        <div className='flex items-center justify-between'>
          <span className='font-semibold text-gray-50'>
            Already have an account?
          </span>
          <ButtonDiv
            className='text-button-primary-text hover:text-button-primary-text-hover font-semibold'
            variant='primary'
            size='sm'
          >
            Log in
          </ButtonDiv>
        </div>
      </div>
      <div className='absolute inset-0 bg-white/10 dark:bg-black/10 transition-opacity duration-300 group-hover:opacity-20 dark:group-hover:opacity-0' />
    </motion.button>
  )

  const [contentRef, { height }] = useMeasure()

  /**
   * Due to the nature of how the screens are rendered, using absolute positioning
   * on the login initial screen, responsive height is not possible.
   *
   * For this reason, we calculate the height of the internal content + footer height.
   */
  const containerComputedHeight = useMemo(() => {
    return height + HEIGHT_CONFIG.LOGIN_FOOTER
  }, [height])

  const contentProps =
    mode === 'login'
      ? loginResult.dialogContentProps
      : walletsResult?.dialogContentProps

  const Footer =
    mode === 'login'
      ? loginResult.isInitialState
        ? loginFooter
        : null
      : walletsResult?.isInitialState
        ? walletsFooter
        : null

  const isLoginInitialState = mode === 'login' && loginResult.isInitialState
  const isWalletsInitialState =
    mode === 'wallets' && walletsResult?.isInitialState

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        {...contentProps}
        closeAction={() => {
          contentProps?.closeAction?.()
          onOpenChange(false)
        }}
        className={cn(
          contentProps?.className,
          'max-w-md rounded-[32px] overflow-y-auto space-y-0 h-auto',
        )}
        headerClassName={cn({
          'min-h-20 absolute top-0 left-0 right-0': isLoginInitialState,
          'max-sm:pt-4': mode === 'wallets',
        })}
        style={{
          ...contentProps?.style,
          /**
           * Reset the maxWidth, maxHeight, height, and width to unset
           * to allow the Dialog to manange them, as we are using scrollWithOverlay
           */
          maxWidth: 'unset',
          maxHeight: 'unset',
          height: 'unset',
          width: 'unset',
        }}
        scrollWithOverlay
      >
        <div
          className={cn('relative flex flex-col w-full', {
            'justify-end': isLoginInitialState,
          })}
          style={{
            height: isLoginInitialState ? containerComputedHeight : 'unset',
            minHeight: isWalletsInitialState ? '512px' : 'unset',
            maxHeight: isWalletsInitialState ? '768px' : 'unset',
          }}
        >
          <AnimatePresence mode='popLayout'>
            <motion.div
              key={mode}
              initial={enableAnimation ? { y: '200%' } : false}
              animate={{ y: 0 }}
              exit={{ y: '200%' }}
              style={{
                height: isLoginInitialState ? height : 'unset',
              }}
              transition={{
                type: 'tween',
                ease: [0.34, 0.94, 0.1, 1],
                duration: 0.5,
                stiffness: 300,
                damping: 30,
              }}
              className={cn('relative top-0 overflow-hidden left-0 right-0', {
                'absolute rounded-[32px] z-10 bg-foreground':
                  isLoginInitialState,
              })}
            >
              <div
                className='relative h-fit'
                ref={contentRef as Ref<HTMLDivElement>}
              >
                {mode === 'login'
                  ? loginResult.content
                  : walletsResult?.content}
              </div>
            </motion.div>
          </AnimatePresence>
          {Footer}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface OnboardingDialogRootProps {
  isVercelPreview?: boolean
  chainType?: ChainType
}

export function OnboardingDialogRoot({
  isVercelPreview,
  chainType,
}: OnboardingDialogRootProps) {
  const [dialogState, setDialogState] = useAtom(onboardingDialogAtom)
  const isOpen = dialogState.open
  const setIsOpen = (open: boolean) => {
    setDialogState(open ? openOnboardingDialog() : closeOnboardingDialog())
  }
  const chainTypeFromStore = useAtomValue(selectWalletsDialogChainTypeAtom)

  return (
    <OnboardingDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      isVercelPreview={isVercelPreview}
      chainType={chainTypeFromStore ?? chainType}
    />
  )
}
